import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import PromoBanner from '../../components/layout/PromoBanner';
import ColorPicker from '../../components/theme/ColorPicker';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { 
  Image,
  Video,
  Palette,
  Eye,
  Save,
  Loader2,
  Upload,
  Type,
  Calendar,
  Sparkles,
  RotateCcw,
  Music,
  Volume2,
  VolumeX,
  Users,
  Play,
  Wand2,
  Zap
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const PromoBannerSettingsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // AI Generation state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiStyle, setAiStyle] = useState('professional');
  const [aiTheme, setAiTheme] = useState('default');
  const [generatedImage, setGeneratedImage] = useState(null);

  const [banner, setBanner] = useState({
    enabled: false,
    banner_type: 'color', // color, image, video, audio
    backgroundColor: '#1a365d',
    backgroundImage: '',
    videoUrl: '',
    videoAutoplay: true,
    videoMuted: true,
    audioUrl: '',
    audioAutoplay: false,
    overlayColor: '#000000',
    overlayOpacity: 0.5,
    title: 'Joyeuses fêtes de fin d\'année !',
    titleColor: '#ffffff',
    titleSize: 'text-base',
    subtitle: '',
    subtitleColor: '#ffffff',
    link: '',
    linkText: '',
    startDate: '',
    endDate: '',
    targetAudience: ['all']
  });

  useEffect(() => {
    fetchBanner();
  }, []);

  const fetchBanner = async () => {
    try {
      const res = await api.get('/settings/promo-banner');
      if (res.data) {
        setBanner({
          enabled: res.data.enabled || false,
          banner_type: res.data.banner_type || 'color',
          backgroundColor: res.data.backgroundColor || '#1a365d',
          backgroundImage: res.data.backgroundImage || '',
          videoUrl: res.data.videoUrl || '',
          videoAutoplay: res.data.videoAutoplay !== false,
          videoMuted: res.data.videoMuted !== false,
          audioUrl: res.data.audioUrl || '',
          audioAutoplay: res.data.audioAutoplay || false,
          overlayColor: res.data.overlayColor || '#000000',
          overlayOpacity: res.data.overlayOpacity || 0.5,
          title: res.data.title || '',
          titleColor: res.data.titleColor || '#ffffff',
          titleSize: res.data.titleSize || 'text-base',
          subtitle: res.data.subtitle || '',
          subtitleColor: res.data.subtitleColor || '#ffffff',
          link: res.data.link || '',
          linkText: res.data.linkText || '',
          startDate: res.data.startDate || '',
          endDate: res.data.endDate || '',
          targetAudience: res.data.targetAudience || ['all']
        });
      }
    } catch (error) {
      console.error('Error fetching banner:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/promo-banner', banner);
      toast.success('Bannière mise à jour ! Rafraîchissez la page pour voir les changements.');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setBanner({
      enabled: false,
      banner_type: 'color',
      backgroundColor: '#1a365d',
      backgroundImage: '',
      videoUrl: '',
      videoAutoplay: true,
      videoMuted: true,
      audioUrl: '',
      audioAutoplay: false,
      overlayColor: '#000000',
      overlayOpacity: 0.5,
      title: '',
      titleColor: '#ffffff',
      titleSize: 'text-base',
      subtitle: '',
      subtitleColor: '#ffffff',
      link: '',
      linkText: '',
      startDate: '',
      endDate: '',
      targetAudience: ['all']
    });
    setGeneratedImage(null);
  };

  const handleGenerateAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Veuillez entrer une description pour l\'image');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.post('/settings/generate-banner', {
        prompt: aiPrompt,
        style: aiStyle,
        theme: aiTheme
      });

      if (response.data.success && response.data.image_base64) {
        const imageUrl = `data:image/png;base64,${response.data.image_base64}`;
        setGeneratedImage(imageUrl);
        setBanner({ 
          ...banner, 
          banner_type: 'image',
          backgroundImage: imageUrl 
        });
        toast.success('Image générée avec succès !');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const toggleAudience = (audience) => {
    if (audience === 'all') {
      setBanner({ ...banner, targetAudience: ['all'] });
    } else {
      let newAudience = banner.targetAudience.filter(a => a !== 'all');
      if (newAudience.includes(audience)) {
        newAudience = newAudience.filter(a => a !== audience);
      } else {
        newAudience.push(audience);
      }
      if (newAudience.length === 0) {
        newAudience = ['all'];
      }
      setBanner({ ...banner, targetAudience: newAudience });
    }
  };

  // Video examples
  const videoExamples = [
    { label: 'Traîneau du Père Noël', url: 'https://www.youtube.com/watch?v=EKrOQHaySCQ' },
    { label: 'Neige qui tombe', url: 'https://www.youtube.com/watch?v=G_puaBm6DaM' },
    { label: 'Feu de cheminée', url: 'https://www.youtube.com/watch?v=ZY3J3Y_OU0w' }
  ];

  const audienceOptions = [
    { id: 'all', label: 'Tout le monde', icon: Users },
    { id: 'client', label: 'Clients', icon: Users },
    { id: 'team_member', label: 'Membres équipe', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1a365d] flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              Bannière Promotionnelle
            </h1>
            <p className="text-[#64748b] mt-1">
              Créez des bannières avec image, vidéo ou audio pour vos campagnes
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2 border border-[#e2e8f0]">
              <span className="text-sm text-[#64748b]">Bannière active</span>
              <Switch
                checked={banner.enabled}
                onCheckedChange={(checked) => setBanner({ ...banner, enabled: checked })}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Panel */}
          <Card className="card lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                Configuration de la bannière
              </CardTitle>
              <CardDescription>
                Personnalisez l'arrière-plan, l'overlay, le texte et le ciblage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="background" className="space-y-6">
                <TabsList className="bg-[#f1f5f9] flex-wrap h-auto">
                  <TabsTrigger value="background" className="flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Arrière-plan
                  </TabsTrigger>
                  <TabsTrigger value="ai" className="flex items-center gap-2">
                    <Wand2 className="w-4 h-4" />
                    Générer IA
                  </TabsTrigger>
                  <TabsTrigger value="overlay" className="flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Overlay
                  </TabsTrigger>
                  <TabsTrigger value="content" className="flex items-center gap-2">
                    <Type className="w-4 h-4" />
                    Contenu
                  </TabsTrigger>
                  <TabsTrigger value="schedule" className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Planification
                  </TabsTrigger>
                  <TabsTrigger value="audience" className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Ciblage
                  </TabsTrigger>
                </TabsList>

                {/* Background Tab */}
                <TabsContent value="background" className="space-y-6">
                  <div>
                    <Label className="mb-3 block">Type d'arrière-plan</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { id: 'color', label: 'Couleur unie', icon: Palette },
                        { id: 'image', label: 'Image PNG', icon: Image },
                        { id: 'video', label: 'Vidéo YouTube', icon: Video },
                        { id: 'audio', label: 'Audio MP3', icon: Music }
                      ].map((option) => (
                        <button
                          key={option.id}
                          onClick={() => setBanner({ ...banner, banner_type: option.id })}
                          className={cn(
                            "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all",
                            banner.banner_type === option.id
                              ? "border-purple-500 bg-purple-50"
                              : "border-[#e2e8f0] hover:border-purple-300"
                          )}
                        >
                          <option.icon className={cn(
                            "w-6 h-6",
                            banner.banner_type === option.id ? "text-purple-600" : "text-[#64748b]"
                          )} />
                          <span className={cn(
                            "text-xs font-medium text-center",
                            banner.banner_type === option.id ? "text-purple-600" : "text-[#64748b]"
                          )}>
                            {option.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Background */}
                  {banner.banner_type === 'color' && (
                    <ColorPicker
                      label="Couleur de fond"
                      color={banner.backgroundColor}
                      onChange={(c) => setBanner({ ...banner, backgroundColor: c })}
                    />
                  )}

                  {/* Image Background */}
                  {banner.banner_type === 'image' && (
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block">URL de l'image (PNG recommandé)</Label>
                        <div className="flex gap-2">
                          <Input
                            value={banner.backgroundImage}
                            onChange={(e) => setBanner({ ...banner, backgroundImage: e.target.value })}
                            placeholder="https://example.com/banner.png"
                            className="flex-1"
                          />
                          <Button variant="outline" size="icon" disabled>
                            <Upload className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-[#64748b] mt-1">
                          Utilisez une image panoramique (ex: 1920x100px) pour un meilleur rendu
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Video Background */}
                  {banner.banner_type === 'video' && (
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block">URL de la vidéo YouTube</Label>
                        <Input
                          value={banner.videoUrl}
                          onChange={(e) => setBanner({ ...banner, videoUrl: e.target.value })}
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                      </div>
                      
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={banner.videoAutoplay}
                            onCheckedChange={(checked) => setBanner({ ...banner, videoAutoplay: checked })}
                          />
                          <Label className="flex items-center gap-1">
                            <Play className="w-4 h-4" />
                            Lecture automatique
                          </Label>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={banner.videoMuted}
                            onCheckedChange={(checked) => setBanner({ ...banner, videoMuted: checked })}
                          />
                          <Label className="flex items-center gap-1">
                            {banner.videoMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                            {banner.videoMuted ? 'Sans son' : 'Avec son'}
                          </Label>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="mb-2 block text-[#64748b]">Exemples de vidéos</Label>
                        <div className="flex flex-wrap gap-2">
                          {videoExamples.map((ex) => (
                            <button
                              key={ex.label}
                              onClick={() => setBanner({ ...banner, videoUrl: ex.url })}
                              className="px-3 py-1.5 text-xs rounded-full border border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors"
                            >
                              {ex.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Audio Background */}
                  {banner.banner_type === 'audio' && (
                    <div className="space-y-4">
                      <ColorPicker
                        label="Couleur de fond (derrière le lecteur)"
                        color={banner.backgroundColor}
                        onChange={(c) => setBanner({ ...banner, backgroundColor: c })}
                      />
                      
                      <div>
                        <Label className="mb-2 block">URL du fichier audio (MP3)</Label>
                        <Input
                          value={banner.audioUrl}
                          onChange={(e) => setBanner({ ...banner, audioUrl: e.target.value })}
                          placeholder="https://example.com/musique.mp3"
                        />
                        <p className="text-xs text-[#64748b] mt-1">
                          Un bouton Play/Stop sera affiché pour contrôler la lecture
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={banner.audioAutoplay}
                          onCheckedChange={(checked) => setBanner({ ...banner, audioAutoplay: checked })}
                        />
                        <Label className="flex items-center gap-1">
                          <Play className="w-4 h-4" />
                          Lecture automatique (non recommandé)
                        </Label>
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* AI Generation Tab */}
                <TabsContent value="ai" className="space-y-6">
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-purple-100">
                        <Wand2 className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-900">Génération par IA</h4>
                        <p className="text-sm text-purple-700">
                          Décrivez l'image que vous souhaitez et notre IA la créera pour vous !
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block">Description de l'image</Label>
                    <Textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="Ex: Bannière de Noël avec un traineau du père noël volant dans un ciel étoilé..."
                      className="min-h-[100px]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="mb-2 block">Style</Label>
                      <Select value={aiStyle} onValueChange={setAiStyle}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professionnel</SelectItem>
                          <SelectItem value="festive">Festif</SelectItem>
                          <SelectItem value="modern">Moderne</SelectItem>
                          <SelectItem value="minimal">Minimaliste</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-2 block">Thème</Label>
                      <Select value={aiTheme} onValueChange={setAiTheme}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Par défaut</SelectItem>
                          <SelectItem value="christmas">Noël</SelectItem>
                          <SelectItem value="halloween">Halloween</SelectItem>
                          <SelectItem value="summer">Été</SelectItem>
                          <SelectItem value="spring">Printemps</SelectItem>
                          <SelectItem value="corporate">Corporate</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    onClick={handleGenerateAI}
                    disabled={generating || !aiPrompt.trim()}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Génération en cours... (30-60s)
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Générer l'image
                      </>
                    )}
                  </Button>

                  {/* Generated image preview */}
                  {generatedImage && (
                    <div className="space-y-3">
                      <Label className="block">Image générée</Label>
                      <div className="rounded-xl overflow-hidden border border-[#e2e8f0]">
                        <img 
                          src={generatedImage} 
                          alt="Generated banner" 
                          className="w-full h-32 object-cover"
                        />
                      </div>
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Image appliquée comme arrière-plan !
                      </p>
                    </div>
                  )}

                  {/* Quick prompts */}
                  <div>
                    <Label className="mb-2 block text-[#64748b]">Suggestions rapides</Label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "Bannière de Noël avec neige et étoiles",
                        "Décoration Halloween élégante",
                        "Promotion été avec soleil et plage",
                        "Bannière corporate moderne"
                      ].map((prompt) => (
                        <button
                          key={prompt}
                          onClick={() => setAiPrompt(prompt)}
                          className="px-3 py-1.5 text-xs rounded-full border border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                {/* Overlay Tab */}
                <TabsContent value="overlay" className="space-y-6">
                  {(banner.banner_type === 'image' || banner.banner_type === 'video') && (
                    <>
                      <ColorPicker
                        label="Couleur de l'overlay"
                        color={banner.overlayColor}
                        onChange={(c) => setBanner({ ...banner, overlayColor: c })}
                      />
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <Label>Opacité de l'overlay</Label>
                          <span className="text-sm font-mono text-[#64748b]">
                            {Math.round(banner.overlayOpacity * 100)}%
                          </span>
                        </div>
                        <Slider
                          value={[banner.overlayOpacity * 100]}
                          onValueChange={(v) => setBanner({ ...banner, overlayOpacity: v[0] / 100 })}
                          min={0}
                          max={100}
                          step={5}
                        />
                        <p className="text-xs text-[#64748b] mt-1">
                          Augmentez l'opacité pour améliorer la lisibilité du texte
                        </p>
                      </div>
                    </>
                  )}
                  
                  {(banner.banner_type === 'color' || banner.banner_type === 'audio') && (
                    <div className="text-center py-8 text-[#64748b]">
                      <Palette className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>L'overlay n'est disponible qu'avec les fonds image ou vidéo</p>
                    </div>
                  )}
                </TabsContent>

                {/* Content Tab */}
                <TabsContent value="content" className="space-y-6">
                  <div>
                    <Label className="mb-2 block">Titre principal</Label>
                    <Input
                      value={banner.title}
                      onChange={(e) => setBanner({ ...banner, title: e.target.value })}
                      placeholder="Joyeuses fêtes de fin d'année !"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <ColorPicker
                      label="Couleur du titre"
                      color={banner.titleColor}
                      onChange={(c) => setBanner({ ...banner, titleColor: c })}
                    />
                    
                    <div>
                      <Label className="mb-2 block">Taille du titre</Label>
                      <Select
                        value={banner.titleSize}
                        onValueChange={(v) => setBanner({ ...banner, titleSize: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text-xs">Très petit</SelectItem>
                          <SelectItem value="text-sm">Petit</SelectItem>
                          <SelectItem value="text-base">Normal</SelectItem>
                          <SelectItem value="text-lg">Grand</SelectItem>
                          <SelectItem value="text-xl">Très grand</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block">Sous-titre (optionnel)</Label>
                    <Input
                      value={banner.subtitle}
                      onChange={(e) => setBanner({ ...banner, subtitle: e.target.value })}
                      placeholder="Profitez de nos offres spéciales"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="mb-2 block">Lien (optionnel)</Label>
                      <Input
                        value={banner.link}
                        onChange={(e) => setBanner({ ...banner, link: e.target.value })}
                        placeholder="/promotions"
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block">Texte du bouton</Label>
                      <Input
                        value={banner.linkText}
                        onChange={(e) => setBanner({ ...banner, linkText: e.target.value })}
                        placeholder="Découvrir"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Schedule Tab */}
                <TabsContent value="schedule" className="space-y-6">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
                    <p className="text-sm text-blue-700">
                      <strong>Planification :</strong> Définissez une période de diffusion pour votre bannière. 
                      Elle sera automatiquement activée/désactivée aux dates indiquées.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="mb-2 block">Date de début</Label>
                      <Input
                        type="date"
                        value={banner.startDate}
                        onChange={(e) => setBanner({ ...banner, startDate: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block">Date de fin</Label>
                      <Input
                        type="date"
                        value={banner.endDate}
                        onChange={(e) => setBanner({ ...banner, endDate: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  {banner.startDate && banner.endDate && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                      <p className="text-sm text-green-700">
                        La bannière sera visible du <strong>{new Date(banner.startDate).toLocaleDateString('fr-FR')}</strong> au <strong>{new Date(banner.endDate).toLocaleDateString('fr-FR')}</strong>
                      </p>
                    </div>
                  )}
                </TabsContent>

                {/* Audience Tab */}
                <TabsContent value="audience" className="space-y-6">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
                    <p className="text-sm text-amber-700">
                      <strong>Ciblage :</strong> Choisissez qui peut voir cette bannière. 
                      Sélectionnez "Tout le monde" ou des audiences spécifiques.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {audienceOptions.map((option) => (
                      <div
                        key={option.id}
                        className={cn(
                          "flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all",
                          banner.targetAudience.includes(option.id)
                            ? "border-purple-500 bg-purple-50"
                            : "border-[#e2e8f0] hover:border-purple-300"
                        )}
                        onClick={() => toggleAudience(option.id)}
                      >
                        <Checkbox
                          checked={banner.targetAudience.includes(option.id)}
                          onCheckedChange={() => toggleAudience(option.id)}
                        />
                        <option.icon className={cn(
                          "w-5 h-5",
                          banner.targetAudience.includes(option.id) ? "text-purple-600" : "text-[#64748b]"
                        )} />
                        <span className={cn(
                          "font-medium",
                          banner.targetAudience.includes(option.id) ? "text-purple-600" : "text-[#64748b]"
                        )}>
                          {option.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              {/* Action Buttons */}
              <div className="flex justify-between pt-6 mt-6 border-t border-[#e2e8f0]">
                <Button variant="outline" onClick={handleReset}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Réinitialiser
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview Panel */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Aperçu en direct
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-xl overflow-hidden border border-[#e2e8f0]">
                <PromoBanner 
                  campaign={{ ...banner, enabled: true }} 
                  height="h-12"
                />
              </div>
              
              <div className="bg-[#f8fafc] rounded-xl p-4">
                <h4 className="text-sm font-medium text-[#1a365d] mb-2">Configuration</h4>
                <div className="space-y-2 text-xs text-[#64748b]">
                  <div className="flex justify-between">
                    <span>Type</span>
                    <span className="font-medium text-[#1a365d]">
                      {banner.banner_type === 'color' ? 'Couleur' : 
                       banner.banner_type === 'image' ? 'Image' : 
                       banner.banner_type === 'video' ? 'Vidéo' : 'Audio'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Status</span>
                    <span className={cn(
                      "font-medium",
                      banner.enabled ? "text-green-600" : "text-red-600"
                    )}>
                      {banner.enabled ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  {banner.banner_type === 'video' && (
                    <>
                      <div className="flex justify-between">
                        <span>Autoplay</span>
                        <span className="font-medium text-[#1a365d]">
                          {banner.videoAutoplay ? 'Oui' : 'Non'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Son</span>
                        <span className="font-medium text-[#1a365d]">
                          {banner.videoMuted ? 'Muet' : 'Actif'}
                        </span>
                      </div>
                    </>
                  )}
                  {banner.targetAudience.length > 0 && (
                    <div className="flex justify-between">
                      <span>Audience</span>
                      <span className="font-medium text-[#1a365d]">
                        {banner.targetAudience.includes('all') ? 'Tous' : banner.targetAudience.join(', ')}
                      </span>
                    </div>
                  )}
                  {banner.startDate && (
                    <div className="flex justify-between">
                      <span>Période</span>
                      <span className="font-medium text-[#1a365d]">
                        {banner.startDate} → {banner.endDate || '...'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs text-amber-700">
                  <strong>Note :</strong> Lorsque la bannière est active, le ticker d'actualités est automatiquement remplacé.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default PromoBannerSettingsPage;
